import { AlertCircle, Package, RefreshCw, ShoppingBag, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import {
  STATUS_VALUES,
  getAuto86Dashboard,
  markDishAvailable,
  setAlternativeSuggestion,
} from './smartAutoApi';

// Dummy data for display (same shape as API response) - COMMENTED OUT
// const DUMMY_DISHES = [
//   { dish_id: 'd1', dish_name: 'Paneer Sandwich', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS_VALUES.SOLD_OUT_AUTO, reason: 'Tomatoes at 0', alternative_suggestion_name: 'Veg Burger', alternative_suggestion_id: 'd2', production_template_id: 'pt1', last_updated: new Date(Date.now() - 15 * 60000).toISOString() },
//   { dish_id: 'd2', dish_name: 'Veg Burger', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS_VALUES.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: 'pt2', last_updated: new Date(Date.now() - 60 * 60000).toISOString() },
//   { dish_id: 'd3', dish_name: 'French Fries', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS_VALUES.SOLD_OUT_MANUAL, reason: 'Manual', alternative_suggestion_name: 'Veg Burger', alternative_suggestion_id: 'd2', production_template_id: 'pt3', last_updated: new Date(Date.now() - 30 * 60000).toISOString() },
//   { dish_id: 'd4', dish_name: 'Pizza Margherita', site_id: 'site-002', site_name: 'Indiranagar Branch', status: STATUS_VALUES.SOLD_OUT_AUTO, reason: 'Mozzarella at critical', alternative_suggestion_name: 'Coffee', alternative_suggestion_id: 'd5', production_template_id: 'pt4', last_updated: new Date(Date.now() - 10 * 60000).toISOString() },
//   { dish_id: 'd5', dish_name: 'Coffee', site_id: 'site-002', site_name: 'Indiranagar Branch', status: STATUS_VALUES.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: null, last_updated: new Date(Date.now() - 120 * 60000).toISOString() },
//   { dish_id: 'd6', dish_name: 'Cappuccino', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS_VALUES.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: null, last_updated: new Date(Date.now() - 45 * 60000).toISOString() },
//   { dish_id: 'd7', dish_name: 'Onion Rings', site_id: 'site-002', site_name: 'Indiranagar Branch', status: STATUS_VALUES.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: 'pt5', last_updated: new Date(Date.now() - 90 * 60000).toISOString() },
// ];

// const DUMMY_AT_RISK_ITEMS = [
//   { item_id: 'st1', item_name: 'Tomatoes', current_quantity: 8, critical_threshold: 10, reorder_threshold: 20, dishes_affected: ['Paneer Sandwich', 'Salad'], site_id: 'site-001', site_name: 'Koramangala Outlet' },
//   { item_id: 'st2', item_name: 'Mozzarella', current_quantity: 5, critical_threshold: 5, reorder_threshold: 15, dishes_affected: ['Pizza Margherita', 'Pizza Pepperoni'], site_id: 'site-002', site_name: 'Indiranagar Branch' },
// ];

// function getDummyDashboard(selectedSite, statusFilter) {
//   let filtered = [...DUMMY_DISHES];
//   if (selectedSite) filtered = filtered.filter((d) => d.site_id === selectedSite);
//   if (statusFilter === 'sold_out') {
//     filtered = filtered.filter((d) => d.status === STATUS_VALUES.SOLD_OUT_AUTO || d.status === STATUS_VALUES.SOLD_OUT_MANUAL);
//   } else if (statusFilter === 'available') {
//     filtered = filtered.filter((d) => d.status === STATUS_VALUES.AVAILABLE);
//   }
//   const soldOutAuto = DUMMY_DISHES.filter((d) => d.status === STATUS_VALUES.SOLD_OUT_AUTO).length;
//   const soldOutManual = DUMMY_DISHES.filter((d) => d.status === STATUS_VALUES.SOLD_OUT_MANUAL).length;
//   return {
//     summary: {
//       dishes_sold_out_auto: soldOutAuto,
//       dishes_sold_out_manual: soldOutManual,
//       items_at_or_below_critical: 3,
//       last_auto86_run_at: new Date(Date.now() - 5 * 60000).toISOString(),
//     },
//     dishes: filtered,
//     at_risk_items: selectedSite ? DUMMY_AT_RISK_ITEMS.filter((i) => i.site_id === selectedSite) : DUMMY_AT_RISK_ITEMS,
//   };
// }

const STATUS_LABELS = {
  [STATUS_VALUES.AVAILABLE]: 'Available',
  [STATUS_VALUES.SOLD_OUT_AUTO]: 'Sold out – auto',
  [STATUS_VALUES.SOLD_OUT_MANUAL]: 'Sold out – manual',
};

const STATUS_BADGE_CLASS = {
  [STATUS_VALUES.AVAILABLE]: 'bg-emerald-100 text-emerald-800',
  [STATUS_VALUES.SOLD_OUT_AUTO]: 'bg-amber-100 text-amber-800',
  [STATUS_VALUES.SOLD_OUT_MANUAL]: 'bg-gray-200 text-gray-700',
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const SmartAutoRecorder = () => {
  // Log screen view
  useScreenLogging('SmartAutoRecorder', {
    component: 'SmartAutoRecorder',
    screenType: 'auto86-menu-availability',
  });

  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | sold_out | available
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // dish_id when action in progress
  const [alternativeModal, setAlternativeModal] = useState(null); // { dish_id, dish_name, site_id, current_alternative_id }
  const [alternativeOptions, setAlternativeOptions] = useState([]);
  const [selectedAlternativeId, setSelectedAlternativeId] = useState('');
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }

  const fetchSites = useCallback(async () => {
    setIsSitesLoading(true);
    try {
      const response = await getAllSites();
      const list = Array.isArray(response?.sites)
        ? response.sites
        : Array.isArray(response)
        ? response
        : [];
      const sorted = [...list].sort((a, b) =>
        (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
      );
      setSites(
        sorted.length > 0
          ? sorted
          : [
              { site_id: 'site-001', site_name: 'Koramangala Outlet' },
              { site_id: 'site-002', site_name: 'Indiranagar Branch' },
            ]
      );
    } catch (e) {
      console.error('SmartAuto: fetch sites error', e);
      setSites([
        { site_id: 'site-001', site_name: 'Koramangala Outlet' },
        { site_id: 'site-002', site_name: 'Indiranagar Branch' },
      ]);
    } finally {
      setIsSitesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    // site_id is required by the API
    if (!selectedSite) {
      setError(null);
      setData({
        summary: {
          dishes_sold_out_auto: 0,
          dishes_sold_out_manual: 0,
          items_at_or_below_critical: 0,
          last_auto86_run_at: null,
        },
        dishes: [],
        at_risk_items: [],
      });
      setIsLoading(false);
      return;
    }

    try {
      const result = await getAuto86Dashboard({
        site_id: selectedSite,
        status_filter: statusFilter === 'all' ? undefined : statusFilter,
      });
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load dashboard');
        // Set empty data on error
        setData({
          summary: {
            dishes_sold_out_auto: 0,
            dishes_sold_out_manual: 0,
            items_at_or_below_critical: 0,
            last_auto86_run_at: null,
          },
          dishes: [],
          at_risk_items: [],
        });
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard');
      // Set empty data on error
      setData({
        summary: {
          dishes_sold_out_auto: 0,
          dishes_sold_out_manual: 0,
          items_at_or_below_critical: 0,
          last_auto86_run_at: null,
        },
        dishes: [],
        at_risk_items: [],
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite, statusFilter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // PUT /restaurant/dish/{dish_id} — body: { sold_out_status: false, is_automatically_86ed: false, sold_out_reason: "" }
  const handleMarkAvailable = async (dish) => {
    setActionLoading(dish.dish_id);
    try {
      const result = await markDishAvailable({
        dish_id: dish.dish_id,
        site_id: dish.site_id || selectedSite || undefined,
      });
      if (result.success) {
        setToast({ message: 'Dish marked as available.', type: 'success' });
        await loadDashboard();
      } else {
        setToast({ message: result.message || 'Failed to mark dish as available.', type: 'error' });
      }
    } catch (e) {
      setToast({ message: e.message || 'Failed to mark dish as available.', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const openAlternativeModal = (dish) => {
    const options = (data?.dishes || [])
      .filter((d) => d.dish_id !== dish.dish_id && d.status === STATUS_VALUES.AVAILABLE)
      .map((d) => ({ dish_id: d.dish_id, dish_name: d.dish_name }));
    setAlternativeOptions(options);
    setSelectedAlternativeId(dish.alternative_suggestion_id || '');
    setAlternativeModal({
      dish_id: dish.dish_id,
      dish_name: dish.dish_name,
      site_id: dish.site_id,
      current_alternative_name: dish.alternative_suggestion_name,
    });
  };

  // PUT /restaurant/dish/{dish_id} — body: { alternative_suggestion_item_id: "<ALT_DISH_ID>" }
  const handleSetAlternative = async () => {
    if (!alternativeModal) return;
    setActionLoading(alternativeModal.dish_id);
    try {
      const result = await setAlternativeSuggestion({
        dish_id: alternativeModal.dish_id,
        site_id: alternativeModal.site_id || selectedSite || undefined,
        alternative_dish_id: selectedAlternativeId || null,
      });
      if (result.success) {
        setToast({ message: 'Alternative suggestion updated.', type: 'success' });
        setAlternativeModal(null);
        await loadDashboard();
      } else {
        setToast({ message: result.message || 'Failed to update alternative.', type: 'error' });
      }
    } catch (e) {
      setToast({ message: e.message || 'Failed to update alternative.', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const summary = data?.summary || {};
  const dishes = data?.dishes || [];
  const atRiskItems = data?.at_risk_items || [];
  const lastRunAt = summary.last_auto86_run_at
    ? formatRelativeTime(summary.last_auto86_run_at)
    : '—';

  // Create a mapping from site_id to site_name for lookup
  const siteIdToName = useMemo(() => {
    const map = {};
    sites.forEach((s) => {
      const id = s.site_id || s.id;
      const name = s.site_name || s.name;
      if (id) map[id] = name || id; // Fallback to id if name not available
    });
    return map;
  }, [sites]);

  // Helper function to get site name from site_id
  const getSiteName = (siteId) => {
    if (!siteId) return '—';
    return siteIdToName[siteId] || siteId;
  };

  return (
    <div className="w-full h-full">
      {/* Header + Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Auto-86 / Menu availability</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            View what is 86'd, why, and suggested alternatives
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={isSitesLoading}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm min-w-[180px]"
          >
            <option value="">Select a site</option>
            {sites.map((s) => (
              <option key={s.site_id || s.id} value={s.site_id || s.id}>
                {s.site_name || s.name || s.site_id}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            <option value="all">Show all</option>
            <option value="sold_out">Show only 86'd</option>
            <option value="available">Show only available</option>
          </select>

          <button
            type="button"
            onClick={() => loadDashboard()}
            disabled={isLoading}
            className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {toast && (
        <div
          role="alert"
          className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {toast.message}
        </div>
      )}

      {error && (
        <div className={`mb-4 p-4 rounded-lg border text-sm ${
          error.includes('CORS Error') 
            ? 'bg-red-50 border-red-300 text-red-800' 
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium mb-1">
                {error.includes('CORS Error') ? 'CORS Configuration Error' : 'Error Loading Dashboard'}
              </p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1 flex items-center gap-1`}>
            <AlertCircle className="w-4 h-4 text-amber-600" />
            Sold out (auto-86'd)
          </p>
          <p className={`${typography.h4} text-black`}>
            {isLoading ? '—' : summary.dishes_sold_out_auto ?? 0}
          </p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1 flex items-center gap-1`}>
            <ShoppingBag className="w-4 h-4 text-gray-600" />
            Sold out (manual)
          </p>
          <p className={`${typography.h4} text-black`}>
            {isLoading ? '—' : summary.dishes_sold_out_manual ?? 0}
          </p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1 flex items-center gap-1`}>
            <Package className="w-4 h-4 text-red-600" />
            Items at/below critical
          </p>
          <p className={`${typography.h4} text-black`}>
            {isLoading ? '—' : summary.items_at_or_below_critical ?? 0}
          </p>
        </Card>
        <Card variant="solid" className="p-4">
          <p className={`${typography.sh3} text-gray-500 mb-1`}>Last auto-86 run</p>
          <p className={`${typography.h4} text-black`}>{isLoading ? '—' : lastRunAt}</p>
        </Card>
      </div>

      {/* Main table: dishes */}
      <Card variant="solid" className="p-4 mb-6">
        <h3 className={`${typography.sh2} text-black mb-4`}>Dishes</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="pb-2 pr-4 font-medium">Dish</th>
                <th className="pb-2 pr-4 font-medium">Site</th>
                <th className="pb-2 pr-4 font-medium">Status</th>
                <th className="pb-2 pr-4 font-medium">Reason</th>
                <th className="pb-2 pr-4 font-medium">Alternative suggestion</th>
                <th className="pb-2 pr-4 font-medium">Last updated</th>
                <th className="pb-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : dishes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-400">
                    {!selectedSite ? 'Select a site' : 'No dishes match the filters'}
                  </td>
                </tr>
              ) : (
                dishes.map((row) => (
                  <tr key={row.dish_id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-black font-medium">{row.dish_name}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.site_name || getSiteName(row.site_id) || '—'}</td>
                    <td className="py-2 pr-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          STATUS_BADGE_CLASS[row.status] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {STATUS_LABELS[row.status] ?? row.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-600">{row.status === STATUS_VALUES.AVAILABLE ? '—' : (row.reason ?? '—')}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.alternative_suggestion_name ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-500">{formatRelativeTime(row.last_updated)}</td>
                    <td className="py-2 flex flex-wrap gap-1">
                      {(row.status === STATUS_VALUES.SOLD_OUT_AUTO ||
                        row.status === STATUS_VALUES.SOLD_OUT_MANUAL) && (
                        <button
                          type="button"
                          onClick={() => handleMarkAvailable(row)}
                          disabled={actionLoading === row.dish_id}
                          className="text-emerald-600 hover:text-emerald-700 text-xs font-medium disabled:opacity-60"
                        >
                          Mark available again
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openAlternativeModal(row)}
                        disabled={actionLoading === row.dish_id}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-60"
                      >
                        Change alternative
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* At-risk items panel */}
      {atRiskItems.length > 0 && (
        <Card variant="solid" className="p-4">
          <h3 className={`${typography.sh2} text-black mb-4`}>At-risk items</h3>
          <p className={`${typography.p} text-gray-600 text-sm mb-4`}>
            Stock items above critical but below reorder level. Dishes affected may go 86'd soon.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="pb-2 pr-4 font-medium">Item</th>
                  <th className="pb-2 pr-4 font-medium">Site</th>
                  <th className="pb-2 pr-4 font-medium">Current qty</th>
                  <th className="pb-2 pr-4 font-medium">Critical</th>
                  <th className="pb-2 font-medium">Dishes affected</th>
                </tr>
              </thead>
              <tbody>
                {atRiskItems.map((item) => (
                  <tr key={item.item_id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-black font-medium">{item.item_name}</td>
                    <td className="py-2 pr-4 text-gray-600">{item.site_name || getSiteName(item.site_id) || '—'}</td>
                    <td className="py-2 pr-4 text-amber-700 font-medium">{item.current_quantity}</td>
                    <td className="py-2 pr-4 text-gray-600">{item.critical_threshold}</td>
                    <td className="py-2 text-gray-600">
                      {Array.isArray(item.dishes_affected)
                        ? item.dishes_affected.join(', ')
                        : item.dishes_affected ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Change alternative modal — portal to viewport */}
      {alternativeModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50"
            onClick={() => setAlternativeModal(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="alternative-modal-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 id="alternative-modal-title" className={`${typography.h4} text-black`}>
                  Change alternative for {alternativeModal.dish_name}
                </h2>
                <button
                  type="button"
                  onClick={() => setAlternativeModal(null)}
                  className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Current: {alternativeModal.current_alternative_name || 'None'}
              </p>
              <select
                value={selectedAlternativeId}
                onChange={(e) => setSelectedAlternativeId(e.target.value)}
                className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm mb-4"
              >
                <option value="">None</option>
                {alternativeOptions.map((opt) => (
                  <option key={opt.dish_id} value={opt.dish_id}>
                    {opt.dish_name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAlternativeModal(null)}
                  className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSetAlternative}
                  disabled={actionLoading === alternativeModal.dish_id}
                  className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-60"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SmartAutoRecorder;
